/* eslint-disable @typescript-eslint/no-explicit-any */
/** Team tab, Documents (tab + page), Meetings (tab + page), Portfolio — ported from `other-views.jsx`. */
import { useState, useEffect } from 'react';
import { useData, useDataActions } from '../context/AppDataContext';
import { useCan } from '../context/AuthContext';
import { Ico, Avatar, UserBy, RowMenu, useToast, Modal, monthKeyFromIso, monthKeyLabel, todayMonthKey } from '../components/ui';
import { apiClient } from '../lib/apiClient';
import { downloadCsv, dateStamp } from '../lib/exportUtils';
import { centralFundAmount, compensationRemainder } from '@heros/shared';
import { computePersonWork, buildProjectTouchers } from '../lib/workload';

/** Project picker used by the upload / meeting modals when not already project-scoped. */
function ProjectPicker({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const D = useData();
  return (
    <div className="field">
      <label>Project *</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">— select a project —</option>
        {D.projects.map((p) => <option key={p.id} value={p.id}>{p.code} · {p.shortTitle}</option>)}
      </select>
    </div>
  );
}

const DOC_TYPES = ['Protocol', 'SAP', 'Ethics approval', 'Informed consent', 'CRF', 'Meeting minutes', 'Report', 'Other'];

/** Upload-document modal (brief Feature 11) — wires apiClient.documents.upload via the data action. */
function UploadDocModal({ projectId, onClose }: { projectId: string | null; onClose: () => void }) {
  const D = useData();
  const { uploadDocument } = useDataActions();
  const toast = useToast();
  const [pid, setPid] = useState(projectId || '');
  const [type, setType] = useState('Protocol');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  // Suggest the next auto-version (max existing vN + 1, NOT the row count — a
  // delete must not make the suggestion collide with a surviving label).
  const suggestVersion = (p: string, t: string) => {
    const maxN = D.documents
      .filter((d) => d.project === p && d.type === t)
      .reduce((m, d) => { const n = Number(/^v(\d+)/i.exec(d.version || '')?.[1]); return Number.isFinite(n) ? Math.max(m, n) : m; }, 0);
    return `v${maxN + 1}.0`;
  };
  const [version, setVersion] = useState(() => suggestVersion(projectId || '', 'Protocol'));

  const submit = async () => {
    if (!pid) { toast('Select a project'); return; }
    if (!file) { toast('Choose a file (PDF / JPG / PNG, ≤10 MB)'); return; }
    setBusy(true);
    try {
      await uploadDocument({ projectId: pid, type, category: category || undefined, description: description || undefined, version: version.trim() || undefined, file });
      toast('Document uploaded');
      onClose();
    } catch { /* reportError already toasted */ } finally { setBusy(false); }
  };

  return (
    <Modal title="Upload document" onClose={onClose} footer={
      <>
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn primary" disabled={busy} onClick={() => void submit()}>{busy ? 'Uploading…' : 'Upload'}</button>
      </>
    }>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {!projectId && <ProjectPicker value={pid} onChange={(id) => { setPid(id); setVersion(suggestVersion(id, type)); }} />}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
          <div className="field"><label>Document type *</label>
            <select value={type} onChange={(e) => { setType(e.target.value); setVersion(suggestVersion(pid, e.target.value)); }}>{DOC_TYPES.map((t) => <option key={t}>{t}</option>)}</select>
          </div>
          <div className="field"><label>Version</label><input className="mono" value={version} onChange={(e) => setVersion(e.target.value)} placeholder="e.g. v2.1" title="Auto-suggested — edit freely" /></div>
        </div>
        <div className="field"><label>Category</label><input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. Regulatory, Statistical" /></div>
        <div className="field"><label>Description</label><textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="Optional note about this document" /></div>
        <div className="field"><label>File * <span style={{ color: 'var(--text-3)' }}>(PDF / Word / JPG / PNG, ≤10 MB)</span></label>
          <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </div>
      </div>
    </Modal>
  );
}

/** Meeting-log modal (brief Feature 10) — date/title/participants/notes/decisions/actions/follow-ups.
 *  Pass `meeting` to edit an existing log; omit it to create a new one. */
function MeetingModal({ projectId, meeting, onClose }: { projectId: string | null; meeting?: any; onClose: () => void }) {
  const D = useData();
  const { createMeeting, updateMeeting } = useDataActions();
  const toast = useToast();
  const isEdit = !!meeting;
  const [pid, setPid] = useState(meeting?.project || projectId || '');
  const [title, setTitle] = useState(meeting?.title || '');
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(meeting?.date || today);
  const [startTime, setStartTime] = useState(meeting?.startTime || '');
  const [endTime, setEndTime] = useState(meeting?.endTime || '');
  const [location, setLocation] = useState(meeting?.location || '');
  const [meetingLink, setMeetingLink] = useState(meeting?.meetingLink || '');
  const [status, setStatus] = useState<'scheduled' | 'completed' | 'cancelled'>(meeting?.status || 'scheduled');
  const [attendees, setAttendees] = useState<string[]>(meeting?.attendees || []);
  const [notes, setNotes] = useState(meeting?.notes || '');
  const [decisions, setDecisions] = useState(meeting?.decisionsText || '');
  const [actions, setActions] = useState((meeting?.actionItems || []).join('\n'));
  const [followUps, setFollowUps] = useState((meeting?.followUps || []).join('\n'));
  const [sendEmail, setSendEmail] = useState(false);
  const [busy, setBusy] = useState(false);

  const memberPool = pid ? (D.projects.find((p) => p.id === pid)?.members || []).map((id: string) => D.team.find((u) => u.id === id)).filter(Boolean) : D.team;
  const toggleAtt = (uid: string) => setAttendees((a) => (a.includes(uid) ? a.filter((x) => x !== uid) : [...a, uid]));
  const lines = (s: string) => s.split('\n').map((x) => x.trim()).filter(Boolean);

  const submit = async () => {
    if (!pid) { toast('Select a project'); return; }
    if (!title.trim()) { toast('Meeting title is required'); return; }
    setBusy(true);
    try {
      const payload = {
        title: title.trim(), date, attendees,
        startTime: startTime || undefined,
        endTime: endTime || undefined,
        location: location || undefined,
        meetingLink: meetingLink || undefined,
        status,
        notes: notes || undefined,
        decisionsText: decisions || undefined,
        actionItems: lines(actions),
        followUps: lines(followUps),
        sendEmail,
      };
      const saved = isEdit
        ? await updateMeeting(meeting.id, payload)
        : await createMeeting({ projectId: pid, ...payload });
      // Honest email feedback (graceful degradation when SMTP is not configured).
      if (sendEmail) {
        const mr = saved.mailResult;
        if (!mr || mr.disabled) toast(`${isEdit ? 'Meeting updated' : 'Meeting scheduled'} — email is not configured, no invites sent`);
        else if (mr.sent > 0) toast(`Invitations emailed to ${mr.sent} attendee${mr.sent === 1 ? '' : 's'}${mr.failed ? ` (${mr.failed} failed)` : ''}`);
        else toast(`${isEdit ? 'Meeting updated' : 'Meeting scheduled'} — no attendees to email`);
      } else {
        toast(isEdit ? 'Meeting updated' : 'Meeting saved');
      }
      onClose();
    } catch { /* reportError already toasted */ } finally { setBusy(false); }
  };

  return (
    <Modal title={isEdit ? 'Edit meeting' : 'Schedule meeting'} onClose={onClose} wide footer={
      <>
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn primary" disabled={busy} onClick={() => void submit()}>{busy ? 'Saving…' : (isEdit ? 'Save changes' : 'Schedule')}</button>
      </>
    }>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {!projectId && !isEdit && <ProjectPicker value={pid} onChange={setPid} />}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
          <div className="field"><label>Title *</label><input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Sponsor kick-off / Protocol review" /></div>
          <div className="field"><label>Date *</label><input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <div className="field"><label>Start time</label><input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} /></div>
          <div className="field"><label>End time</label><input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} /></div>
          {isEdit && <div className="field"><label>Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
              <option value="scheduled">Scheduled</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div className="field"><label>Location</label><input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. CTU meeting room / Zoom" /></div>
          <div className="field"><label>Meeting link</label><input className="mono" value={meetingLink} onChange={(e) => setMeetingLink(e.target.value)} placeholder="https://…" /></div>
        </div>
        <div className="field"><label>Participants</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 110, overflowY: 'auto', padding: 4, border: '1px solid var(--border)', borderRadius: 6 }}>
            {memberPool.map((u: any) => (
              <label key={u.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, padding: '2px 6px', borderRadius: 12, cursor: 'pointer', background: attendees.includes(u.id) ? 'var(--accent-soft)' : 'var(--surface)', border: '1px solid ' + (attendees.includes(u.id) ? 'var(--accent)' : 'var(--border)') }}>
                <input type="checkbox" checked={attendees.includes(u.id)} onChange={() => toggleAtt(u.id)} />{u.name}
              </label>
            ))}
          </div>
        </div>
        <div className="field"><label>Notes / minutes</label><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Discussion summary…" /></div>
        <div className="field"><label>Decisions <span style={{ color: 'var(--text-3)' }}>(one per line)</span></label><textarea value={decisions} onChange={(e) => setDecisions(e.target.value)} rows={2} /></div>
        <div className="field"><label>Action items <span style={{ color: 'var(--text-3)' }}>(one per line)</span></label><textarea value={actions} onChange={(e) => setActions(e.target.value)} rows={2} placeholder="Owner — task — due" /></div>
        <div className="field"><label>Follow-up items <span style={{ color: 'var(--text-3)' }}>(one per line)</span></label><textarea value={followUps} onChange={(e) => setFollowUps(e.target.value)} rows={2} /></div>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5, cursor: 'pointer', padding: '6px 0' }}>
          <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
          <span>Email {status === 'cancelled' ? 'a cancellation' : 'an invitation'} to the selected participants <span style={{ color: 'var(--text-3)' }}>(includes a calendar .ics)</span></span>
        </label>
      </div>
    </Modal>
  );
}

export function TeamTab({ p }: { p: any }) {
  const D = useData();
  const toast = useToast();
  const members = p.members.map((id: string) => D.team.find((u) => u.id === id));

  return (
    <div className="card">
      <div className="card-h">
        <h3>Project team — {members.length}</h3>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn sm" onClick={() => {
            const rows = members.filter(Boolean);
            if (!rows.length) return;
            downloadCsv(rows, {
              Name: (m: any) => m.name,
              Role: (m: any) => m.role,
              Email: (m: any) => m.email,
              'Tasks on project': (m: any) => D.timeline.filter((t) => t.project === p.id && t.user === m.id).length,
            }, `${p.code}-roster-${dateStamp()}.csv`);
          }}><Ico.dl /> Roster CSV</button>
          <button
            className="btn primary sm"
            title="Copy the self-registration link to share. New sign-ups need admin approval before they can be added."
            onClick={() => {
              const link = `${window.location.origin}/?register=1`;
              if (navigator.clipboard?.writeText) {
                navigator.clipboard.writeText(link)
                  .then(() => window.alert('Registration link copied:\n' + link + '\n\nShare it. New users appear under Users & Permissions for admin approval.'))
                  .catch(() => window.prompt('Copy this registration link:', link));
              } else {
                window.prompt('Copy this registration link:', link);
              }
            }}
          ><Ico.plus /> Invite (copy link)</button>
        </div>
      </div>
      <div className="card-b flush">
        <table>
          <thead><tr><th>Member</th><th>Role on project</th><th>Email</th><th className="num">Tasks on project</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {members.map((m: any) => {
              const count = D.timeline.filter((t) => t.project === p.id && t.user === m.id).length;
              return (
                <tr key={m.id}>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <Avatar user={m} />
                      <div>
                        <div style={{ fontWeight: 500 }}>{m.name}</div>
                        <div style={{ fontSize: 10.5, color: 'var(--text-2)' }}>{m.role}</div>
                      </div>
                    </span>
                  </td>
                  <td>{m.id === p.pi ? <span className="chip" data-tone="red"><span className="dot" />Project Manager</span> : p.coIs.includes(m.id) ? <span className="chip" data-tone="violet"><span className="dot" />Co-lead</span> : <span className="chip" data-tone="slate"><span className="dot" />Project member</span>}</td>
                  <td className="mono" style={{ fontSize: 11.5, color: 'var(--text-2)' }}>{m.email}</td>
                  <td className="num tabular">{count}</td>
                  <td><span className="chip" data-tone="green"><span className="dot" />Active</span></td>
                  <td><RowMenu items={[
                    { label: 'Copy email', onClick: () => { if (navigator.clipboard?.writeText) { navigator.clipboard.writeText(m.email).then(() => toast('Email copied')).catch(() => window.prompt('Email:', m.email)); } else { window.prompt('Email:', m.email); } } },
                    { label: 'Email member', onClick: () => { window.location.href = `mailto:${m.email}`; } },
                  ]} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Document preview modal. Fetches the file as a blob and previews it via a blob: URL —
 * blob URLs are NOT subject to X-Frame-Options / CSP frame-ancestors, so this avoids the
 * "refused to connect" framing block that hits a direct same-origin iframe src.
 */
function DocPreviewModal({ doc, onClose }: { doc: any; onClose: () => void }) {
  const inline = !!doc.mime && (doc.mime.includes('pdf') || doc.mime.startsWith('image/'));
  const [state, setState] = useState<{ url?: string; error?: string; loading: boolean }>({ loading: true });
  useEffect(() => {
    if (!inline) { setState({ loading: false }); return; }
    let objUrl = ''; let cancelled = false;
    fetch(apiClient.documents.fileUrl(doc.id), { credentials: 'include' })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((b) => { if (!cancelled) { objUrl = URL.createObjectURL(b); setState({ url: objUrl, loading: false }); } })
      .catch((e) => { if (!cancelled) setState({ error: String(e?.message || e), loading: false }); });
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [doc.id, inline]);
  return (
    <Modal title={`${doc.type} · ${doc.version}`} onClose={onClose} wide
      footer={(
        <>
          <a className="btn" href={apiClient.documents.fileUrl(doc.id)} target="_blank" rel="noopener noreferrer">Open in new tab</a>
          <a className="btn primary" href={apiClient.documents.fileUrl(doc.id)} download><Ico.dl /> Download</a>
        </>
      )}>
      {!inline ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-2)', fontSize: 13 }}>In-browser preview isn’t available for this file type{doc.mime ? ` (${doc.mime})` : ''}. Use <strong>Open in new tab</strong> or <strong>Download</strong> below.</div>
      ) : state.loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-2)', fontSize: 13 }}>Loading preview…</div>
      ) : state.error ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--accent)', fontSize: 13 }}>Could not load preview ({state.error}). Use <strong>Open in new tab</strong> or <strong>Download</strong> below.</div>
      ) : doc.mime.startsWith('image/') ? (
        <div style={{ textAlign: 'center', background: 'var(--surface-alt)', borderRadius: 8, padding: 8 }}><img src={state.url} alt={doc.type} style={{ maxWidth: '100%', maxHeight: '70vh' }} /></div>
      ) : (
        <iframe title="Document preview" src={state.url} style={{ width: '100%', height: '70vh', border: '1px solid var(--border)', borderRadius: 8 }} />
      )}
    </Modal>
  );
}

export function DocumentsTab({ projectId }: { projectId: string | null }) {
  const D = useData();
  const { deleteDocument } = useDataActions();
  const toast = useToast();
  const canUpload = useCan('doc.upload') === 'allow';
  const canDelete = canUpload || useCan('doc.approve') === 'allow';
  const [upOpen, setUpOpen] = useState(false);
  const docs = D.documents.filter((d) => !projectId || d.project === projectId);
  const types = [...new Set(docs.map((d) => d.type))];
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [preview, setPreview] = useState<any>(null);
  // Fuzzy matcher: lowercase + tokenise the query, keep a doc when EVERY token is a
  // substring of its combined searchable string (type + filename + category + … + uploader).
  const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
  const haystack = (d: any) => {
    const uploaderName = D.team.find((u) => u.id === d.by)?.name ?? '';
    return [d.type, d.originalName, d.category, d.description, d.version, uploaderName].filter(Boolean).join(' ').toLowerCase();
  };
  const filtered = docs.filter((d) => {
    if (filter !== 'all' && d.type !== filter) return false;
    if (!tokens.length) return true;
    const hay = haystack(d);
    return tokens.every((t) => hay.includes(t));
  });

  const statusTone = (s: string) => {
    if (s.includes('Approved')) return 'green';
    if (s.includes('Archived')) return 'slate';
    if (s.includes('Finalised')) return 'blue';
    return 'amber';
  };

  return (
    <div className="card">
      <div className="card-h">
        <div>
          <h3>Document repository</h3>
          <div className="sub">Protocol and SAP versions, ethics, consent, CRFs, meeting minutes</div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search file name, type, description…"
            style={{ width: 240, height: 28 }}
          />
          <select value={filter} onChange={(e) => setFilter(e.target.value)} style={{ width: 'auto', height: 28 }}>
            <option value="all">All types</option>
            {types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <button className="btn primary sm" disabled={!canUpload} title={canUpload ? 'Upload a document' : 'You do not have permission to upload documents'} onClick={() => setUpOpen(true)}><Ico.plus /> Upload</button>
        </div>
      </div>
      {upOpen && <UploadDocModal projectId={projectId} onClose={() => setUpOpen(false)} />}
      {preview && <DocPreviewModal doc={preview} onClose={() => setPreview(null)} />}
      <div className="sub" style={{ padding: '6px 14px 0' }}>{filtered.length} of {docs.length} documents</div>
      <div className="card-b flush">
        <table>
          <thead><tr><th>Document</th><th>Version</th><th>Status</th><th>Uploaded</th><th>By</th><th className="num">Size</th><th></th></tr></thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-3)', padding: '20px 0' }}>No documents match your search.</td></tr>
            )}
            {filtered.map((d) => (
              <tr key={d.id} className="hover-row">
                <td title={d.originalName || undefined} style={{ maxWidth: 280 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Ico.doc /> <span style={{ fontWeight: 500 }}>{d.type}</span>
                    {d.category && <span className="chip" style={{ fontSize: 10 }}>{d.category}</span>}
                  </span>
                  {d.originalName && (
                    <div className="mono" style={{ fontSize: 10.5, color: 'var(--text-3)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.originalName}</div>
                  )}
                </td>
                <td className="mono" style={{ fontSize: 11.5 }}>{d.version}</td>
                <td><span className="chip" data-tone={statusTone(d.status)}><span className="dot" />{d.status}</span></td>
                <td className="mono" style={{ fontSize: 11.5, color: 'var(--text-2)' }}>{d.uploaded}</td>
                <td><UserBy id={d.by} /></td>
                <td className="num mono" style={{ fontSize: 11.5, color: 'var(--text-2)' }}>{d.size}</td>
                <td>
                  <div style={{ display: 'flex', gap: 2 }}>
                    {d.hasFile && <button className="btn ghost icon sm" title="Preview" onClick={() => setPreview(d)}><Ico.eye /></button>}
                    {d.hasFile
                      ? <a className="btn ghost icon sm" title="Download / open" href={apiClient.documents.fileUrl(d.id)} target="_blank" rel="noopener noreferrer"><Ico.dl /></a>
                      : <button className="btn ghost icon sm" title="No file attached (metadata only)" disabled><Ico.dl /></button>}
                    {canDelete && <button className="btn ghost icon sm danger" title="Delete this version" onClick={async () => {
                      if (!window.confirm(`Delete ${d.type} ${d.version}? This permanently removes the file.`)) return;
                      try { await deleteDocument(d.id); toast('Document deleted'); } catch { /* reportError toasted */ }
                    }}><Ico.trash /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function DocumentsPage() {
  const D = useData();
  const canUpload = useCan('doc.upload') === 'allow';
  const [upOpen, setUpOpen] = useState(false);
  return (
    <div className="page-pad fadein">
      <div className="page-head">
        <div>
          <h2>Documents</h2>
          <div className="sub">{D.documents.length} files across {new Set(D.documents.map((d) => d.project)).size} projects</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={() => {
            if (!D.documents.length) return;
            downloadCsv(D.documents, {
              Project: (d: any) => D.projects.find((x) => x.id === d.project)?.code ?? d.project,
              Type: (d: any) => d.type,
              'File name': (d: any) => d.originalName ?? '',
              Version: (d: any) => d.version,
              Status: (d: any) => d.status,
              Uploaded: (d: any) => d.uploaded,
              Size: (d: any) => d.size,
            }, `documents-index-${dateStamp()}.csv`);
          }}><Ico.dl /> Export index</button>
          <button className="btn primary" disabled={!canUpload} onClick={() => setUpOpen(true)}><Ico.plus /> Upload document</button>
        </div>
      </div>
      {upOpen && <UploadDocModal projectId={null} onClose={() => setUpOpen(false)} />}
      <DocumentsTab projectId={null} />
    </div>
  );
}

export function MeetingsTab({ projectId }: { projectId: string | null }) {
  const D = useData();
  const { updateMeeting } = useDataActions();
  const toast = useToast();
  const canEdit = useCan('proj.edit') === 'allow';
  const [mtgOpen, setMtgOpen] = useState(false);
  const meetings = D.meetings.filter((m) => !projectId || m.project === projectId);
  const logs = D.logs.filter((l) => !projectId || l.project === projectId);
  const [view, setView] = useState('meetings');
  const [editMtg, setEditMtg] = useState<any>(null);

  const today = new Date().toISOString().slice(0, 10);
  const byDate = (a: any, b: any) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0);
  const isUpcoming = (m: any) => (m.status ?? 'scheduled') === 'scheduled' && m.date >= today;
  const upcoming = meetings.filter(isUpcoming).sort(byDate);
  const past = meetings.filter((m) => !isUpcoming(m)).sort((a, b) => byDate(b, a));
  const mtgTone = (s?: string) => (s === 'cancelled' ? 'red' : s === 'completed' ? 'slate' : 'green');
  const whenText = (m: any) => `${m.date}${m.startTime ? ` · ${m.startTime}${m.endTime ? `–${m.endTime}` : ''}` : ''}`;

  const quickCancel = async (m: any) => {
    if (!window.confirm(`Cancel "${m.title}"? (Use Edit to also email a cancellation.)`)) return;
    try { await updateMeeting(m.id, { status: 'cancelled' }); toast('Meeting cancelled'); } catch { /* toasted */ }
  };

  const meetingRow = (m: any) => (
    <tr key={m.id} className="hover-row" style={{ cursor: canEdit ? 'pointer' : 'default' }} onClick={() => { if (canEdit) setEditMtg(m); }}>
      <td style={{ fontWeight: 500 }}>{m.title}{m.meetingLink ? <a href={m.meetingLink} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} title="Open meeting link" style={{ marginLeft: 6, fontSize: 11 }}>↗ link</a> : null}{m.location ? <span style={{ marginLeft: 6, fontSize: 10.5, color: 'var(--text-3)' }}>· {m.location}</span> : null}</td>
      <td className="mono" style={{ fontSize: 11.5, color: 'var(--text-2)' }}>{whenText(m)}</td>
      <td><span className="chip" data-tone={mtgTone(m.status)}><span className="dot" />{m.status ?? 'scheduled'}</span></td>
      <td>
        <span style={{ display: 'inline-flex', gap: 2 }}>
          {m.attendees.map((uid: string) => {
            const u = D.team.find((x) => x.id === uid);
            return <span key={uid} style={{ marginRight: -4 }}><Avatar user={u} size="sm" /></span>;
          })}
        </span>
      </td>
      <td className="num tabular">{m.decisions}</td>
      <td className="num tabular">{m.actions}</td>
      <td>
        <div style={{ display: 'flex', gap: 2 }}>
          <button className="btn ghost icon sm" title={canEdit ? 'Edit meeting' : 'You do not have permission to edit'} disabled={!canEdit} onClick={(e) => { e.stopPropagation(); setEditMtg(m); }}><Ico.edit /></button>
          {canEdit && isUpcoming(m) && <button className="btn ghost icon sm danger" title="Cancel meeting" onClick={(e) => { e.stopPropagation(); void quickCancel(m); }}><Ico.close /></button>}
        </div>
      </td>
    </tr>
  );

  const meetingTable = (rows: any[], empty: string) => (
    rows.length ? (
      <table>
        <thead><tr><th>Title</th><th>When</th><th>Status</th><th>Attendees</th><th className="num">Decisions</th><th className="num">Actions</th><th></th></tr></thead>
        <tbody>{rows.map(meetingRow)}</tbody>
      </table>
    ) : <div style={{ padding: '14px 16px', fontSize: 12, color: 'var(--text-3)' }}>{empty}</div>
  );

  return (
    <div className="card">
      <div className="card-h">
        <div className="seg">
          <button className={view === 'meetings' ? 'on' : ''} onClick={() => setView('meetings')}>Meetings</button>
          <button className={view === 'logs' ? 'on' : ''} onClick={() => setView('logs')}>Project logs</button>
        </div>
        <button className="btn primary sm" disabled={!canEdit} title={canEdit ? 'Schedule a meeting' : 'You do not have permission to schedule meetings'} onClick={() => setMtgOpen(true)}><Ico.plus /> Schedule meeting</button>
      </div>
      {mtgOpen && <MeetingModal projectId={projectId} onClose={() => { setMtgOpen(false); setView('meetings'); }} />}
      {editMtg && <MeetingModal projectId={projectId} meeting={editMtg} onClose={() => setEditMtg(null)} />}
      <div className="card-b flush">
        {view === 'meetings' ? (
          <div>
            <div style={{ padding: '8px 16px 2px', fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--text-3)' }}>Upcoming</div>
            {meetingTable(upcoming, 'No upcoming meetings scheduled.')}
            <div style={{ padding: '12px 16px 2px', fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--text-3)' }}>Past &amp; closed</div>
            {meetingTable(past, 'No past meetings yet.')}
          </div>
        ) : (
          <table>
            <thead><tr><th style={{ width: 140 }}>Timestamp</th><th style={{ width: 160 }}>Type</th><th>Message</th><th style={{ width: 160 }}>By</th></tr></thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id}>
                  <td className="mono" style={{ fontSize: 11.5, color: 'var(--text-2)' }}>{l.at}</td>
                  <td><span className="chip" data-tone={l.type.includes('Issue') ? 'red' : l.type.includes('Recruitment') ? 'green' : 'slate'}><span className="dot" />{l.type}</span></td>
                  <td>{l.msg}</td>
                  <td><UserBy id={l.user} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export function MeetingsPage() {
  return (
    <div className="page-pad fadein">
      <div className="page-head">
        <div>
          <h2>Meetings &amp; Logs</h2>
          <div className="sub">Timestamped notes, decisions, action items, and per-project logs</div>
        </div>
      </div>
      <MeetingsTab projectId={null} />
    </div>
  );
}

const PF_ALL = '__all__';

export function PortfolioPage({ openProject }: { openProject: (id: string, tab?: string) => void }) {
  const D = useData();
  const today = new Date().toISOString().slice(0, 10);
  const thb = (v: number) => '฿' + Math.round(v).toLocaleString();
  const daysFromNow = (iso?: string) => {
    if (!iso) return Infinity;
    const d = new Date(iso); if (Number.isNaN(d.getTime())) return Infinity;
    return Math.round((d.getTime() - new Date(today).getTime()) / 86400000);
  };

  // --- derivations shared by every panel (project-level) ---
  const projTasks = (pid: string) => D.timeline.filter((t) => t.project === pid);
  const isClosed = (p: any) => p.status === 'Completed' || p.status === 'Terminated';
  const isDelayed = (p: any) => {
    const ts = projTasks(p.id);
    if (ts.some((t) => t.status === 'delayed')) return true;
    if (p.endDate && p.endDate < today && !isClosed(p) && ts.some((t) => t.status !== 'completed')) return true;
    return false;
  };
  const quotedOf = (p: any): number =>
    p.activeQuotationTotal ?? D.quotations.filter((q) => q.projectId === p.id).reduce((m, q) => Math.max(m, q.grandTotal), 0);

  // --- filter bar state ---
  const [fSponsor, setFSponsor] = useState(PF_ALL);
  const [fStatus, setFStatus] = useState(PF_ALL);
  const [fPm, setFPm] = useState(PF_ALL);
  // Person filter (driven by the Team workload panel): the projects a person actually
  // WORKS ON — task assignee (splits ∪ owner) or compensation recipient — NOT the PM
  // filter (fPm), which is PI-only. Keeps "click a person → see their projects" honest.
  const [fMember, setFMember] = useState(PF_ALL);
  const [fDesign, setFDesign] = useState(PF_ALL);
  const [fProvisional, setFProvisional] = useState<'all' | 'yes' | 'no'>('all');
  const [delayedOnly, setDelayedOnly] = useState(false);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 }>({ key: 'code', dir: 1 });
  // Per-person workload/earnings panel: sort key + whether idle members are shown.
  const [personSort, setPersonSort] = useState<{ key: 'tasks' | 'earned' | 'received'; dir: 1 | -1 }>({ key: 'tasks', dir: -1 });
  const [showIdle, setShowIdle] = useState(false);

  const uniq = (vals: (string | undefined)[]) => [...new Set(vals.filter((v): v is string => !!v))].sort();
  const sponsors = uniq(D.projects.map((p) => p.sponsor));
  const statuses = uniq(D.projects.map((p) => p.status));
  const designs = uniq(D.projects.map((p) => p.design));
  const pms = uniq(D.projects.map((p) => p.pi));

  // Who WORKS ON each project (filter-independent, so it can drive the person filter):
  // a person "touches" a project if they're a task assignee (splits ∪ owner, real work
  // rows only) OR a compensation recipient there. Shared with the Team Workload tab.
  const projectTouchers = buildProjectTouchers(D.timeline, D.compensations, new Set(D.team.map((u) => u.id)));

  const filtered = D.projects.filter((p) => {
    if (fSponsor !== PF_ALL && p.sponsor !== fSponsor) return false;
    if (fStatus !== PF_ALL && p.status !== fStatus) return false;
    if (fPm !== PF_ALL && p.pi !== fPm) return false;
    if (fMember !== PF_ALL && !projectTouchers.get(p.id)?.has(fMember)) return false;
    if (fDesign !== PF_ALL && p.design !== fDesign) return false;
    if (fProvisional === 'yes' && !p.provisional) return false;
    if (fProvisional === 'no' && p.provisional) return false;
    if (delayedOnly && !isDelayed(p)) return false;
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      if (!`${p.code} ${p.title} ${p.shortTitle} ${p.sponsor}`.toLowerCase().includes(needle)) return false;
    }
    return true;
  });

  const anyFilter = fSponsor !== PF_ALL || fStatus !== PF_ALL || fPm !== PF_ALL || fMember !== PF_ALL || fDesign !== PF_ALL || fProvisional !== 'all' || delayedOnly || !!q.trim();
  const resetFilters = () => { setFSponsor(PF_ALL); setFStatus(PF_ALL); setFPm(PF_ALL); setFMember(PF_ALL); setFDesign(PF_ALL); setFProvisional('all'); setDelayedOnly(false); setQ(''); };

  // --- KPI numbers (filtered) ---
  const active = filtered.filter((p) => !isClosed(p)).length;
  const totalQuoted = filtered.reduce((s, p) => s + quotedOf(p), 0);
  const delayedCount = filtered.filter(isDelayed).length;
  const endingSoon = filtered.filter((p) => !isClosed(p) && daysFromNow(p.endDate) >= 0 && daysFromNow(p.endDate) <= 90);

  // --- Money rollups (senior request) ---
  // Total central fund = Σ central-fund skim; Remaining allocation = Σ (actual received − central fund).
  const totalActualReceived = filtered.reduce((s, p) => s + (p.actualReceived ?? 0), 0);
  const totalCentralFund = filtered.reduce((s, p) => s + centralFundAmount(p.actualReceived, p.centralFundPct), 0);
  const totalRemaining = filtered.reduce((s, p) => s + compensationRemainder(p.actualReceived, p.centralFundPct), 0);
  const moneyEntered = filtered.filter((p) => (p.actualReceived ?? 0) > 0).length;

  // --- New-projects-per-month intake chart (by record createdAt) ---
  const intakeByMonth = new Map<number, number>();
  filtered.forEach((p) => {
    if (!p.createdAt) return;
    const k = monthKeyFromIso(String(p.createdAt).slice(0, 10));
    if (!Number.isNaN(k)) intakeByMonth.set(k, (intakeByMonth.get(k) || 0) + 1);
  });
  const intakeChart = (() => {
    const keys = [...intakeByMonth.keys()];
    const tK = todayMonthKey();
    let minK = keys.length ? Math.min(...keys) : tK - 11;
    const maxK = Math.max(tK, keys.length ? Math.max(...keys) : tK);
    if (maxK - minK > 23) minK = maxK - 23; // cap at 24 months
    const bars: { k: number; label: string; count: number }[] = [];
    for (let k = minK; k <= maxK; k++) bars.push({ k, label: monthKeyLabel(k), count: intakeByMonth.get(k) || 0 });
    const peak = bars.reduce((m, b) => Math.max(m, b.count), 0);
    // Total counts EVERY in-view project with a creation date, even months trimmed by the
    // 24-month bar cap, so the subtitle agrees with the header KPIs.
    return { bars, peak, total: [...intakeByMonth.values()].reduce((s, n) => s + n, 0) };
  })();

  // --- analytics panels ---
  const byStatus: Record<string, number> = {};
  filtered.forEach((p) => { byStatus[p.status] = (byStatus[p.status] || 0) + 1; });
  const statusRows = Object.entries(byStatus).sort((a, b) => b[1] - a[1]);

  const sponsorAgg: Record<string, { count: number; quoted: number }> = {};
  filtered.forEach((p) => {
    const key = p.sponsor || '—';
    const a = (sponsorAgg[key] ??= { count: 0, quoted: 0 });
    a.count += 1; a.quoted += quotedOf(p);
  });
  const sponsorRows = Object.entries(sponsorAgg).sort((a, b) => b[1].quoted - a[1].quoted);

  const pmAgg: Record<string, number> = {};
  filtered.filter((p) => !isClosed(p)).forEach((p) => { pmAgg[p.pi] = (pmAgg[p.pi] || 0) + 1; });
  const pmRows = Object.entries(pmAgg).sort((a, b) => b[1] - a[1]);

  const filteredIds = new Set(filtered.map((p) => p.id));
  const upcomingLandmarks = D.timeline
    .filter((t) => filteredIds.has(t.project) && t.status !== 'completed' && daysFromNow(t.startDate) >= 0 && daysFromNow(t.startDate) <= 60)
    .sort((a, b) => (a.startDate < b.startDate ? -1 : 1))
    .slice(0, 14);

  // --- Per-person workload + earnings (senior request: "who does what work more
  //     frequently, and who earns what"). Scoped to the FILTERED projects so this
  //     panel agrees with the KPI cards. Task allocation = every assignee on a task
  //     (splits ∪ owner), excluding milestone/phase markers; earnings mirror the
  //     Compensation page (effectiveEarning), received vs outstanding. Counting lives
  //     in the shared workload helper so the Team Workload tab + profile portfolio
  //     and this panel never drift. ---
  const { rows: personRowsAll } = computePersonWork({
    timeline: D.timeline, compensations: D.compensations, team: D.team, projectIds: filteredIds,
  });
  const personActive = personRowsAll.filter((r) => r.tasks > 0 || r.earned > 0);
  const idleCount = personRowsAll.length - personActive.length;
  const personVisible = showIdle ? personRowsAll : personActive;
  const personTotalTasks = personRowsAll.reduce((s, r) => s + r.tasks, 0);
  const personTotalEarned = personRowsAll.reduce((s, r) => s + r.earned, 0);
  // Average is over ACTIVE members (not the whole roster) on purpose: this panel is
  // scoped to the FILTERED projects, so dividing by everyone would understate the load
  // and over-flag people. (The Dashboard's equity view divides by the whole roster
  // because it is unfiltered + roster-wide — intentional, different scope.)
  const personAvgTasks = personActive.length ? personTotalTasks / personActive.length : 0;
  const personMaxTasks = Math.max(1, ...personRowsAll.map((r) => r.tasks));
  const personMaxEarned = Math.max(1, ...personRowsAll.map((r) => r.earned));
  const personValue = (r: { tasks: number; earned: number; received: number }) =>
    (personSort.key === 'tasks' ? r.tasks : personSort.key === 'received' ? r.received : r.earned);
  const personSorted = [...personVisible].sort((a, b) =>
    (personValue(a) - personValue(b)) * personSort.dir || a.u.name.localeCompare(b.u.name));
  const togglePersonSort = (key: 'tasks' | 'earned' | 'received') =>
    setPersonSort((s) => (s.key === key ? { key, dir: (s.dir === 1 ? -1 : 1) as 1 | -1 } : { key, dir: -1 }));
  const personSortArrow = (key: string) => (personSort.key === key ? (personSort.dir === 1 ? ' ▲' : ' ▼') : '');
  const topCats = (cats: Record<string, number>) =>
    Object.entries(cats).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([c, n]) => `${c} ×${n}`).join(', ');
  const exportPeopleCsv = () => {
    if (!personSorted.length) return;
    downloadCsv(personSorted, {
      Member: (r: any) => r.u.name,
      Role: (r: any) => r.u.role,
      'Task allocations': (r: any) => r.tasks,
      Projects: (r: any) => r.projects.size,
      'Earned (฿)': (r: any) => r.earned,
      'Received (฿)': (r: any) => r.received,
      'Outstanding (฿)': (r: any) => r.outstanding,
    }, `portfolio-people-${dateStamp()}.csv`);
  };

  // --- sortable project table ---
  const sortVal = (p: any): string | number => {
    const ts = projTasks(p.id);
    const done = ts.filter((t) => t.status === 'completed').length;
    switch (sort.key) {
      case 'status': return p.status;
      case 'sponsor': return p.sponsor || '';
      case 'end': return p.endDate || '';
      case 'progress': return ts.length ? done / ts.length : 0;
      default: return p.code;
    }
  };
  const sorted = [...filtered].sort((a, b) => {
    const va = sortVal(a), vb = sortVal(b);
    return (va < vb ? -1 : va > vb ? 1 : 0) * sort.dir;
  });
  const toggleSort = (key: string) => setSort((s) => (s.key === key ? { key, dir: (s.dir === 1 ? -1 : 1) } : { key, dir: 1 }));
  const sortArrow = (key: string) => (sort.key === key ? (sort.dir === 1 ? ' ▲' : ' ▼') : '');

  const exportCsv = () => {
    if (!sorted.length) return;
    downloadCsv(sorted, {
      Code: (p: any) => p.code,
      Title: (p: any) => p.shortTitle,
      Sponsor: (p: any) => p.sponsor,
      Design: (p: any) => p.design,
      Status: (p: any) => p.status,
      'Project Manager': (p: any) => D.team.find((u) => u.id === p.pi)?.name ?? p.pi,
      Start: (p: any) => p.startDate,
      End: (p: any) => p.endDate,
      Delayed: (p: any) => (isDelayed(p) ? 'yes' : 'no'),
      'Quoted (฿)': (p: any) => quotedOf(p),
      Provisional: (p: any) => (p.provisional ? 'yes' : 'no'),
    }, `portfolio-${dateStamp()}.csv`);
  };

  const selStyle = { width: 'auto', height: 28, fontSize: 12 } as const;

  return (
    <div className="page-pad fadein">
      <div className="page-head">
        <div>
          <h2>Portfolio Overview</h2>
          <div className="sub">Unit-wide snapshot for the senior team — {filtered.length} of {D.projects.length} projects{anyFilter ? ' (filtered)' : ''}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={exportCsv} disabled={!sorted.length}><Ico.dl /> Export CSV</button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-b" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code / title / sponsor…" style={{ width: 220, height: 28, fontSize: 12 }} />
          <select value={fSponsor} onChange={(e) => setFSponsor(e.target.value)} style={selStyle}><option value={PF_ALL}>All sponsors</option>{sponsors.map((s) => <option key={s} value={s}>{s}</option>)}</select>
          <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} style={selStyle}><option value={PF_ALL}>All statuses</option>{statuses.map((s) => <option key={s} value={s}>{s}</option>)}</select>
          <select value={fPm} onChange={(e) => setFPm(e.target.value)} style={selStyle}><option value={PF_ALL}>All PMs</option>{pms.map((id) => <option key={id} value={id}>{D.team.find((u) => u.id === id)?.name ?? id}</option>)}</select>
          <select value={fDesign} onChange={(e) => setFDesign(e.target.value)} style={selStyle}><option value={PF_ALL}>All designs</option>{designs.map((d) => <option key={d} value={d}>{d}</option>)}</select>
          <select value={fProvisional} onChange={(e) => setFProvisional(e.target.value as 'all' | 'yes' | 'no')} style={selStyle}><option value="all">Provisional: any</option><option value="no">Active only</option><option value="yes">Provisional only</option></select>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={delayedOnly} onChange={(e) => setDelayedOnly(e.target.checked)} /> Delayed only
          </label>
          {anyFilter && <button className="btn ghost sm" onClick={resetFilters}>Reset</button>}
        </div>
      </div>

      {/* KPI cards (recompute from the filtered set) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 14 }}>
        <div className="stat"><div className="label">Active projects</div><div className="v tabular">{active}</div><div className="d">of {filtered.length} in view</div></div>
        <div className="stat"><div className="label">Total quoted</div><div className="v">{thb(totalQuoted)}</div><div className="d">active/last quotation per project</div></div>
        <div className="stat" style={{ cursor: 'pointer' }} onClick={() => setDelayedOnly((v) => !v)} title="Toggle the 'delayed only' filter"><div className="label">Delayed projects</div><div className="v tabular" style={{ color: delayedCount ? 'var(--danger, #b3261e)' : undefined }}>{delayedCount}</div><div className="d">{delayedOnly ? 'filtering ✓' : 'click to filter'}</div></div>
        <div className="stat" style={{ borderColor: 'var(--accent)' }}><div className="label">Total central fund</div><div className="v" style={{ color: 'var(--accent)' }}>{thb(totalCentralFund)}</div><div className="d">skimmed from {moneyEntered} project{moneyEntered === 1 ? '' : 's'} w/ actuals</div></div>
        <div className="stat"><div className="label">Remaining allocation</div><div className="v">{thb(totalRemaining)}</div><div className="d">received {thb(totalActualReceived)} − central fund</div></div>
        <div className="stat"><div className="label">Ending ≤ 90 days</div><div className="v tabular">{endingSoon.length}</div><div className="d">need close-out planning</div></div>
      </div>

      {/* New-projects-per-month intake trend (by record creation date) */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-h"><h3>New projects per month</h3><span className="sub">{intakeChart.total} project{intakeChart.total === 1 ? '' : 's'} entered in view{anyFilter ? ' (filtered)' : ''}</span></div>
        <div className="card-b">
          {intakeChart.peak === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>No project creation dates available yet.</div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 140, overflowX: 'auto', paddingBottom: 4 }}>
              {intakeChart.bars.map((b) => {
                const isNow = b.k === todayMonthKey();
                const h = b.count === 0 ? 2 : Math.round((b.count / intakeChart.peak) * 108) + 6;
                return (
                  <div key={b.k} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, minWidth: 34 }} title={`${b.label}: ${b.count} new project${b.count === 1 ? '' : 's'}`}>
                    <div style={{ fontSize: 10, color: 'var(--text-2)', fontWeight: 600, height: 12 }}>{b.count || ''}</div>
                    <div style={{ width: 22, height: h, borderRadius: '4px 4px 0 0', background: b.count === 0 ? 'var(--border)' : (isNow ? 'var(--accent)' : 'var(--accent-soft, #cde3ff)'), border: isNow ? '1px solid var(--accent)' : 'none' }} />
                    <div style={{ fontSize: 9, color: isNow ? 'var(--accent)' : 'var(--text-3)', whiteSpace: 'nowrap', transform: 'rotate(-35deg)', transformOrigin: 'center', marginTop: 6, fontWeight: isNow ? 700 : 400 }}>{b.label}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 14 }}>
        {/* Sortable project table */}
        <div className="card">
          <div className="card-h"><h3>Projects</h3><span className="sub">{sorted.length} shown</span></div>
          <div className="card-b flush">
            <table>
              <thead><tr>
                <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('code')}>Code{sortArrow('code')}</th>
                <th>Project</th>
                <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('sponsor')}>Sponsor{sortArrow('sponsor')}</th>
                <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('status')}>Status{sortArrow('status')}</th>
                <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('end')}>End{sortArrow('end')}</th>
                <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('progress')}>Progress{sortArrow('progress')}</th>
              </tr></thead>
              <tbody>
                {sorted.map((p) => {
                  const tasks = projTasks(p.id);
                  const done = tasks.filter((t) => t.status === 'completed').length;
                  const pct = tasks.length ? (done / tasks.length * 100) : 0;
                  const delayed = isDelayed(p);
                  return (
                    <tr key={p.id} className="hover-row" onClick={() => openProject(p.id)}>
                      <td className="mono" style={{ fontWeight: 600 }}>{p.code}</td>
                      <td>{p.shortTitle}{p.provisional ? <span className="chip" data-tone="amber" style={{ marginLeft: 6 }}><span className="dot" />provisional</span> : null}</td>
                      <td style={{ fontSize: 12 }}>{p.sponsor || '—'}</td>
                      <td><span className="chip" data-tone={delayed ? 'red' : 'slate'}><span className="dot" />{delayed ? 'Delayed' : p.status}</span></td>
                      <td className="mono" style={{ fontSize: 11.5, color: daysFromNow(p.endDate) <= 90 && !isClosed(p) ? 'var(--danger, #b3261e)' : 'var(--text-2)' }}>{p.endDate || '—'}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div className="bar-track" style={{ width: 110 }}><div className="bar-fill" style={{ width: Math.min(100, pct) + '%' }} /></div>
                          <span className="mono tabular" style={{ fontSize: 11, color: 'var(--text-2)' }}>{done}/{tasks.length}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!sorted.length && <tr><td colSpan={6} style={{ padding: 16, color: 'var(--text-3)', fontSize: 12 }}>No projects match the current filters.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right rail: distribution + sponsors + PM workload */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="card">
            <div className="card-h"><h3>Status distribution</h3></div>
            <div className="card-b" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {statusRows.map(([s, n]) => (
                <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} title="Click to filter by this status" onClick={() => setFStatus((cur) => (cur === s ? PF_ALL : s))}>
                  <span style={{ flex: '0 0 150px', fontSize: 12, fontWeight: fStatus === s ? 700 : 400 }}>{s}</span>
                  <div className="bar-track" style={{ flex: 1 }}><div className="bar-fill" style={{ width: (filtered.length ? n / filtered.length * 100 : 0) + '%', background: 'var(--accent)' }} /></div>
                  <span className="tabular mono" style={{ fontSize: 11.5, width: 24, textAlign: 'right' }}>{n}</span>
                </div>
              ))}
              {!statusRows.length && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>No projects in view.</div>}
            </div>
          </div>

          <div className="card">
            <div className="card-h"><h3>By sponsor</h3><span className="sub">count · quoted</span></div>
            <div className="card-b flush">
              <table>
                <tbody>
                  {sponsorRows.map(([s, a]) => (
                    <tr key={s} className="hover-row" style={{ cursor: 'pointer' }} title="Click to filter by this sponsor" onClick={() => setFSponsor((cur) => (cur === s ? PF_ALL : s))}>
                      <td style={{ fontSize: 12, fontWeight: fSponsor === s ? 700 : 400 }}>{s}</td>
                      <td className="num tabular" style={{ fontSize: 12 }}>{a.count}</td>
                      <td className="num mono tabular" style={{ fontSize: 11.5, color: 'var(--text-2)' }}>{thb(a.quoted)}</td>
                    </tr>
                  ))}
                  {!sponsorRows.length && <tr><td style={{ padding: 12, color: 'var(--text-3)', fontSize: 12 }}>—</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <div className="card-h"><h3>Active workload by PM</h3></div>
            <div className="card-b flush">
              <table>
                <tbody>
                  {pmRows.map(([id, n]) => {
                    const u = D.team.find((x) => x.id === id);
                    return (
                      <tr key={id} className="hover-row" style={{ cursor: 'pointer' }} onClick={() => setFPm((cur) => (cur === id ? PF_ALL : id))}>
                        <td><span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}><Avatar user={u} size="sm" />{u?.name ?? id}</span></td>
                        <td className="num tabular" style={{ fontSize: 12 }}>{n}</td>
                      </tr>
                    );
                  })}
                  {!pmRows.length && <tr><td style={{ padding: 12, color: 'var(--text-3)', fontSize: 12 }}>No active projects.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Team workload & earnings — who does what work more frequently, and who earns what */}
      <div className="card" style={{ marginTop: 14 }}>
        <div className="card-h">
          <h3>Team workload &amp; earnings</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span className="sub">
              {personActive.length} active · avg {personAvgTasks.toFixed(1)} task{personAvgTasks.toFixed(1) === '1.0' ? '' : 's'}/person · {personTotalTasks} allocation{personTotalTasks === 1 ? '' : 's'} · {thb(personTotalEarned)} earned{anyFilter ? ' (filtered)' : ''}
            </span>
            <button className="btn ghost sm" onClick={exportPeopleCsv} disabled={!personSorted.length}><Ico.dl /> CSV</button>
          </div>
        </div>
        <div className="card-b flush">
          <table>
            <thead><tr>
              <th>Team member</th>
              <th className="num" style={{ cursor: 'pointer' }} onClick={() => togglePersonSort('tasks')} title="Number of task allocations (every person on a task, milestone/phase markers excluded)">Tasks{personSortArrow('tasks')}</th>
              <th className="num">Projects</th>
              <th className="num" style={{ cursor: 'pointer' }} onClick={() => togglePersonSort('earned')} title="Total compensation earned across the in-view projects">Earned{personSortArrow('earned')}</th>
              <th className="num" style={{ cursor: 'pointer' }} onClick={() => togglePersonSort('received')} title="Compensation already marked received">Received{personSortArrow('received')}</th>
              <th className="num" title="Earned but not yet marked received">Outstanding</th>
            </tr></thead>
            <tbody>
              {personSorted.map((r) => {
                const taskPct = (r.tasks / personMaxTasks) * 100;
                const earnPct = (r.earned / personMaxEarned) * 100;
                const heavy = personAvgTasks > 0 && r.tasks > personAvgTasks * 1.4;
                const light = personAvgTasks > 0 && r.tasks > 0 && r.tasks < personAvgTasks * 0.6;
                const selected = fMember === r.u.id;
                return (
                  <tr key={r.u.id} className="hover-row" style={{ cursor: 'pointer', background: selected ? 'var(--surface-alt)' : undefined }} onClick={() => setFMember((cur) => (cur === r.u.id ? PF_ALL : r.u.id))} title="Click to filter the portfolio to projects this person works on (click again to clear)">
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <Avatar user={r.u} size="sm" />
                        <span style={{ minWidth: 0 }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: selected ? 800 : 600 }}>
                            {r.u.name}
                            {heavy && <span className="chip" data-tone="red" style={{ fontSize: 9.5, padding: '1px 6px' }}><span className="dot" />Heavy</span>}
                            {light && <span className="chip" data-tone="amber" style={{ fontSize: 9.5, padding: '1px 6px' }}><span className="dot" />Light</span>}
                          </span>
                          <span style={{ fontSize: 10.5, color: 'var(--text-2)' }}>{r.u.role}</span>
                        </span>
                      </span>
                    </td>
                    <td className="num" style={{ minWidth: 120 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }} title={topCats(r.cats) || 'No tasks in view'}>
                        <div className="bar-track" style={{ width: 70 }}><div className="bar-fill" style={{ width: Math.max(r.tasks ? 3 : 0, taskPct) + '%', background: 'var(--accent)' }} /></div>
                        <span className="tabular" style={{ fontWeight: 700, width: 22, textAlign: 'right' }}>{r.tasks}</span>
                      </div>
                    </td>
                    <td className="num tabular" style={{ fontSize: 12, color: 'var(--text-2)' }}>{r.projects.size}</td>
                    <td className="num" style={{ minWidth: 130 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
                        <div className="bar-track" style={{ width: 60 }}><div className="bar-fill" style={{ width: Math.max(r.earned ? 3 : 0, earnPct) + '%', background: 'var(--t-green-fg)' }} /></div>
                        <span className="mono tabular" style={{ fontWeight: 600, fontSize: 11.5 }}>{thb(r.earned)}</span>
                      </div>
                    </td>
                    <td className="num mono tabular" style={{ fontSize: 11.5, color: 'var(--t-green-fg)' }}>{r.received ? thb(r.received) : '—'}</td>
                    <td className="num mono tabular" style={{ fontSize: 11.5, color: r.outstanding ? 'var(--text)' : 'var(--text-3)' }}>{r.outstanding ? thb(r.outstanding) : '—'}</td>
                  </tr>
                );
              })}
              {!personSorted.length && <tr><td colSpan={6} style={{ padding: 16, color: 'var(--text-3)', fontSize: 12 }}>No team members hold work in the filtered projects.</td></tr>}
            </tbody>
          </table>
        </div>
        {idleCount > 0 && (
          <div className="card-b" style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>
            <button className="btn ghost sm" onClick={() => setShowIdle((v) => !v)}>
              {showIdle ? 'Hide' : 'Show'} {idleCount} member{idleCount === 1 ? '' : 's'} with no workload in view
            </button>
          </div>
        )}
      </div>

      {/* Upcoming landmarks across the filtered portfolio */}
      <div className="card" style={{ marginTop: 14 }}>
        <div className="card-h"><h3>Upcoming landmarks</h3><span className="sub">starting in the next 60 days</span></div>
        <div className="card-b flush">
          <table>
            <thead><tr><th>Project</th><th>Phase / task</th><th>Starts</th><th className="num">In</th></tr></thead>
            <tbody>
              {upcomingLandmarks.map((t) => {
                const p = D.projects.find((x) => x.id === t.project);
                const inDays = daysFromNow(t.startDate);
                return (
                  <tr key={t.id} className="hover-row" onClick={() => p && openProject(p.id, 'timeline')}>
                    <td className="mono" style={{ fontWeight: 600 }}>{p?.code ?? '—'}</td>
                    <td>{t.name}</td>
                    <td className="mono" style={{ fontSize: 11.5, color: 'var(--text-2)' }}>{t.startDate}</td>
                    <td className="num tabular" style={{ fontSize: 12 }}>{inDays === 0 ? 'today' : `${inDays}d`}</td>
                  </tr>
                );
              })}
              {!upcomingLandmarks.length && <tr><td colSpan={4} style={{ padding: 16, color: 'var(--text-3)', fontSize: 12 }}>No phases starting in the next 60 days for the filtered projects.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
